import React, { createContext, useContext, useState, useEffect } from "react";
import { AuthenticationResult } from "@azure/msal-browser";
import { UserContext } from "./UserContext";
import { LoggingContext } from "./LoggingContext";
import { FatalError } from "../services/ErrorHelpers";
import { IUserRoles, IActionLinks } from "./dataFetchers/storeRequests";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";

import { fetchAndParseCompanyCodes } from "./dataFetchers/companyCodes";
import { fetchAndParseCurrencyCodes } from "./dataFetchers/currencyCodes";
import { fetchAndParseDocumentTypes } from "./dataFetchers/documentTypes";
import { fetchAndParseFiscalYears } from "./dataFetchers/fiscalYears";
import { fetchAndParseUserRoles } from "./dataFetchers/userRoles";
import { IWeightages, fetchAndParseActionLinks } from "./dataFetchers/actionLinks";
import { SLLoadingStatus } from "../utilities/utilities";

export interface SubledgerContextData {
  actionLinks: {
    status: SLLoadingStatus;
    weightages: IWeightages;
    actions: IActionLinks[];
  };
  companyCodes: {
    status: SLLoadingStatus;
    value: string[];
  };
  currencyCodes: {
    status: SLLoadingStatus;
    value: string[];
  };
  documentTypes: {
    status: SLLoadingStatus;
    value: string[];
  };
  fiscalYears: {
    status: SLLoadingStatus;
    value: string[];
  };
  userRoles: {
    status: SLLoadingStatus;
    value: IUserRoles;
  };
}

const initialState: SubledgerContextData = {
  actionLinks: {
    status: SLLoadingStatus.Idle,
    weightages: {
      SendToPoster: 0,
      Attachments: 0,
      Delete: 0,
      Recall: 0,
      AddPosters: 0,
      SubmitForReview: 0,
      ApproveSLEntries: 0,
      NeedsClarification: 0,
      ExportToExcel: 0,
      Save: 0,
      Validate: 0,
      ClearForm: 0
    },
    actions: []
  },
  companyCodes: {
    status: SLLoadingStatus.Idle,
    value: []
  },
  currencyCodes: {
    status: SLLoadingStatus.Idle,
    value: []
  },
  documentTypes: {
    status: SLLoadingStatus.Idle,
    value: []
  },
  fiscalYears: {
    status: SLLoadingStatus.Idle,
    value: []
  },
  userRoles: {
    status: SLLoadingStatus.Idle,
    value: {
      isAdmin: false,
      hasPostingAccess: false,
      isReadOnly: false,
      isReviewer: false
    }
  }
};

const SubledgerContext = createContext(initialState);
const { Provider } = SubledgerContext;

async function loader<T>(
  getTokenFn: () => Promise<AuthenticationResult | undefined>,
  appInsights: ApplicationInsights,
  fetcherFunction: (getTokenFn: () => Promise<AuthenticationResult | undefined>) => Promise<T>,
  loadingDispatch: React.Dispatch<React.SetStateAction<SLLoadingStatus>>,
  valuesDispatch: React.Dispatch<React.SetStateAction<T>>
) {
  loadingDispatch(SLLoadingStatus.Pending);
  try {
    const apiData = await fetcherFunction(getTokenFn);
    valuesDispatch(apiData);
    loadingDispatch(SLLoadingStatus.Resolved);
  } catch (e) {
    appInsights.trackException({
      exception: e
    });
    loadingDispatch(SLLoadingStatus.Rejected);
  }
}

interface ActionLinksState {
  weightages: IWeightages;
  actions: IActionLinks[];
}

const SubledgerProvider: React.FC = (props) => {
  const { appInsights } = useContext(LoggingContext);
  if (!appInsights) {
    throw new FatalError("Please use a LoggingContext Provider.");
  }
  const { accessToken } = useContext(UserContext);
  if (!accessToken) {
    throw new FatalError("Please use a UserContext Provider.");
  }
  const [actionLinksLoadingStatus, setActionLinksLoadingStatus] = useState<SLLoadingStatus>(SLLoadingStatus.Idle);
  const [actionLinks, setActionLinks] = useState<ActionLinksState>({
    actions: initialState.actionLinks.actions,
    weightages: initialState.actionLinks.weightages
  });
  const [companyCodesLoadingStatus, setCompanyCodesLoadingStatus] = useState<SLLoadingStatus>(SLLoadingStatus.Idle);
  const [companyCodes, setCompanyCodes] = useState<string[]>([]);
  const [currencyCodesLoadingStatus, setCurrencyCodesLoadingStatus] = useState<SLLoadingStatus>(SLLoadingStatus.Idle);
  const [currencyCodes, setCurrencyCodes] = useState<string[]>([]);
  const [documentTypesLoadingStatus, setDocumentTypesLoadingStatus] = useState<SLLoadingStatus>(SLLoadingStatus.Idle);
  const [documentTypes, setDocumentTypes] = useState<string[]>([]);
  const [fiscalYearsLoadingStatus, setFiscalYearsLoadingStatus] = useState<SLLoadingStatus>(SLLoadingStatus.Idle);
  const [fiscalYears, setFiscalYears] = useState<string[]>([]);
  const [userRolesLoadingStatus, setUserRolesLoadingStatus] = useState<SLLoadingStatus>(SLLoadingStatus.Idle);
  const [userRoles, setUserRoles] = useState<IUserRoles>(initialState.userRoles.value);

  useEffect(() => {
    loader(accessToken, appInsights, fetchAndParseActionLinks, setActionLinksLoadingStatus, setActionLinks);
    loader(accessToken, appInsights, fetchAndParseCompanyCodes, setCompanyCodesLoadingStatus, setCompanyCodes);
    loader(accessToken, appInsights, fetchAndParseCurrencyCodes, setCurrencyCodesLoadingStatus, setCurrencyCodes);
    loader(accessToken, appInsights, fetchAndParseDocumentTypes, setDocumentTypesLoadingStatus, setDocumentTypes);
    loader(accessToken, appInsights, fetchAndParseFiscalYears, setFiscalYearsLoadingStatus, setFiscalYears);
    loader(accessToken, appInsights, fetchAndParseUserRoles, setUserRolesLoadingStatus, setUserRoles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Provider
      value={{
        actionLinks: {
          status: actionLinksLoadingStatus,
          actions: actionLinks.actions,
          weightages: actionLinks.weightages
        },
        companyCodes: {
          status: companyCodesLoadingStatus,
          value: companyCodes
        },
        currencyCodes: {
          status: currencyCodesLoadingStatus,
          value: currencyCodes
        },
        documentTypes: {
          status: documentTypesLoadingStatus,
          value: documentTypes
        },
        fiscalYears: {
          status: fiscalYearsLoadingStatus,
          value: fiscalYears
        },
        userRoles: {
          status: userRolesLoadingStatus,
          value: userRoles
        }
      }}
    >
      {props.children}
    </Provider>
  );
};

export { SubledgerProvider, SubledgerContext };
